// Copyright (c) Docugami, Inc. All rights reserved.

import { WorkPathOverflowMenu, WorkPathOverflowMenuProps } from './WorkPathItem';
import { WorkPath } from 'networking/WorkPathApi';
import { LimitsContainer } from 'components/common/LimitsContainer';
import { DetailsHeader, DetailsList, DetailsListLayoutMode, IDetailsHeaderProps, IRenderFunction, SelectionMode, Sticky, StickyPositionType, CheckboxVisibility, IColumn, ColumnActionsMode, Link, IDetailsListStyles, IconButton, ScrollablePane, ThemeProvider, IDetailsRowProps } from '@fluentui/react';
import { homeGray, primaryTheme, secondaryTheme, transparentDetailsList } from 'components/shell/presentation/AppStyles';
import styled from "styled-components";
import { SortedState, SortWorkPathByField, WorkPathsViewType } from "./WorkPathsViewer";
import dayjs from "dayjs";
import { DisabledCss } from "components/common/DgCommonStyledComponents";
import { DgFlexRow } from "components/common/DgCommonStyledComponents"
import { getClusterNameById } from 'store/state_helpers';
import { useAppDispatch } from 'hooks/redux';
import { OverflowTooltipText } from 'components/common/OverflowText';
import { PropertyCallbacks } from 'components/file_management/presentation/DocumentViewer';
import { typeToLabel } from './WorkPathsHelper';

const ListViewContainer = styled.div`
    position: relative;
    height: 100%;
    flex: 1;
`;

const CommandContainer = styled.div`
    flex-shrink: 0;
    margin-top: -11px;
    margin-bottom: -11px;
    padding-top: 1px;
    padding-bottom: 1px;
    white-space: nowrap;
    overflow: hidden;
    width: 0;
    align-items: center;
    justify-content: center;
    vertical-align: center;
`;

const NameContainer = styled.div`
    display: flex;
    flex-direction: row;
    width: 100%;
    justify-content: space-between;
    :hover {
        .hover-commands {
            overflow: unset;
            width: auto;
        }
    }
`;

const NameText = styled(Link)`
    text-overflow: ellipsis;
    overflow: hidden;
    white-space: nowrap;
    -webkit-box-flex: 1;
    flex: 1 1 auto;
    color: ${primaryTheme.palette.black};

    :hover {
        color: ${primaryTheme.palette.black};
    }
`;

const listStyles: Partial<IDetailsListStyles> = {
    root: {
        maxWidth: '100%'
    },
    contentWrapper: {
        ".ms-DetailsRow:hover": {
            ".hover-commands": {
                overflow: "unset",
                width: "auto"
            }
        }
    }
}

interface WorkPathsListViewProps {
    items: WorkPath[],
    onSelect: (id: string) => void,
    isItemLimited: (item: WorkPath) => boolean,
    menuProps: (id: string) => WorkPathOverflowMenuProps | undefined,
    sortItems: (typeToSortBy: SortWorkPathByField) => void,
    links?: PropertyCallbacks<WorkPath, string>;
    validateActions?: PropertyCallbacks<WorkPath, boolean>;
    sortedState?: SortedState
}

export const WorkPathsListView = (props: WorkPathsListViewProps): JSX.Element => {
    const { sortedBy, ascending: sortedAscending } = props.sortedState ?? {};
    const dispatch = useAppDispatch();

    const onRenderDetailsHeader: IRenderFunction<IDetailsHeaderProps> = (props?: IDetailsHeaderProps): JSX.Element | null => {
        if (!props) {
            return null;
        }

        return (
            <Sticky stickyPosition={StickyPositionType.Header} isScrollSynced>
                <DetailsHeader {...props} />
            </Sticky>
        );
    };

    const columns: IColumn[] = [
        {
            key: 'name',
            name: 'Name',
            fieldName: 'name',
            minWidth: 250,
            isRowHeader: true,
            isSorted: sortedBy === SortWorkPathByField.Name,
            isSortedDescending: sortedBy === SortWorkPathByField.Name && !sortedAscending,
            columnActionsMode: ColumnActionsMode.clickable,
            onRender: (item: WorkPath) => {
                const menuProps = props.menuProps(item.id);
                const nameLink = getColumnLink('name', item);
                return (
                    <NameContainer>
                        <NameText href={nameLink} onClick={() => props.onSelect?.(item.id)}>{item.name}</NameText>
                        <CommandContainer className="hover-commands">
                            { !!menuProps && <WorkPathOverflowMenu smallIcon {...menuProps} /> }
                        </CommandContainer>
                    </NameContainer>
                )
            },
            onColumnClick: () => props.sortItems(SortWorkPathByField.Name)
        },
        {
            key: 'docset',
            name: 'Doc Set',
            minWidth: 150,
            isSorted: sortedBy === SortWorkPathByField.DocSet,
            isSortedDescending: sortedBy === SortWorkPathByField.DocSet && !sortedAscending,
            columnActionsMode: ColumnActionsMode.clickable,
            onRender: (item: WorkPath) => {
                const docSetName = dispatch(getClusterNameById(item.clusterId));
                return <OverflowTooltipText content={docSetName}>{docSetName}</OverflowTooltipText>
            },
            onColumnClick: () => props.sortItems(SortWorkPathByField.DocSet)
        },
        {
            key: 'item_type',
            name: 'Type',
            minWidth: 150,
            isSorted: sortedBy === SortWorkPathByField.Type,
            isSortedDescending: sortedBy === SortWorkPathByField.Type && !sortedAscending,
            columnActionsMode: ColumnActionsMode.clickable,
            onRender: (item: WorkPath) => {
                return <OverflowTooltipText content={typeToLabel.get(item.workPathType)}>{typeToLabel.get(item.workPathType)}</OverflowTooltipText>
            },
            onColumnClick: () => props.sortItems(SortWorkPathByField.Type)
        },
        {
            key: 'last_modified',
            name: 'Last Modified',
            minWidth: 150,
            isSorted: sortedBy === SortWorkPathByField.LastModified,
            isSortedDescending: sortedBy === SortWorkPathByField.LastModified && !sortedAscending,
            columnActionsMode: ColumnActionsMode.clickable,
            onRender: (item: WorkPath) => {
                const lastModifiedTime = dayjs(item.lastModified);
                let lastModifiedText = lastModifiedTime.format('MMMM D, YYYY');
                // If last modified within 24hrs, show time from now in more readable format
                if (dayjs().diff(lastModifiedTime, 'days') < 1) {
                    lastModifiedText = lastModifiedTime.fromNow();
                }
                return <OverflowTooltipText content={lastModifiedText}>{lastModifiedText}</OverflowTooltipText>
            },
            onColumnClick: () => props.sortItems(SortWorkPathByField.LastModified)
        }
    ]

    function getColumnLink<P extends keyof WorkPath>(key: P, item: WorkPath): string | undefined {
        const link = props.links?.[key]?.(item) as string;
        const validate = props.validateActions?.[key];
        if (!!link && !!validate && !validate(item)) {
            return;
        }

        return link;
    }

    function onRenderRow(rowProps?: IDetailsRowProps, defaultRender?: (props?: IDetailsRowProps) => JSX.Element | null) {
        if (!rowProps || !defaultRender) {
            return null;
        }

        const item = rowProps.item as WorkPath;
        return (
            <LimitsContainer key={item.id} actionName={`view or modify ${item.name}`} limitsDisabled={!props.isItemLimited(item)} >
                {defaultRender(rowProps)}
            </LimitsContainer>
        )
    }

    return (
        <ListViewContainer>
            <ScrollablePane>
                <ThemeProvider theme={transparentDetailsList} >
                    <DetailsList
                        items={props.items}
                        theme={secondaryTheme}
                        selectionMode={SelectionMode.none}
                        layoutMode={DetailsListLayoutMode.justified}
                        columns={columns}
                        styles={listStyles}
                        onRenderDetailsHeader={onRenderDetailsHeader}
                        checkboxVisibility={CheckboxVisibility.hidden}
                        onRenderRow={onRenderRow} />
                </ThemeProvider>
            </ScrollablePane>
        </ListViewContainer>
    );
}

const ViewSwitcherContainer = styled.div<{ disabled?: boolean }>`
    display: flex;
    flex-direction: row;
    margin-left: auto;
    ${props => props.disabled && DisabledCss};
`;

interface ViewSwitcherProps {
    selectedViewType: WorkPathsViewType;
    setView: (viewType: WorkPathsViewType) => void;
    disabled?: boolean;
}

export const ViewSwitcher = (props: ViewSwitcherProps): JSX.Element => {
    return (
        <ViewSwitcherContainer disabled={props.disabled} >
            <IconButton
                title="List View"
                checked={props.selectedViewType === WorkPathsViewType.List}
                iconProps={{ iconName: "BulletedList" }}
                styles={{ icon: { color: homeGray } }}
                onClick={() => props.setView(WorkPathsViewType.List)} />
            <IconButton
                title="Grid View"
                checked={props.selectedViewType === WorkPathsViewType.Icon}
                iconProps={{ iconName: "ViewAll" }}
                styles={{ icon: { color: homeGray } }}
                onClick={() => props.setView(WorkPathsViewType.Icon)} />
        </ViewSwitcherContainer>
    )
}

const ContainerRoot = styled.div`
    box-sizing: border-box;
    overflow: auto;
`;

const DgFlexRowWrap = styled(DgFlexRow)`
    flex-wrap: wrap;
    overflow: hidden;
    gap: 10px;
    justify-content: flex-start;
`;

interface WorkPathGridViewProps {
    children: JSX.Element[];
}

/**
 * A grid container of items.
 * @param props
 */
export const WorkPathGridView = (props: WorkPathGridViewProps): JSX.Element => {
    return (
        <ContainerRoot>
            <DgFlexRow>
                <DgFlexRowWrap>
                    {props.children}
                </DgFlexRowWrap>
            </DgFlexRow>
        </ContainerRoot>
    )
}
